import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getConfiguracionesPlantelService,
  createConfiguracionPlantelService,
  updateConfiguracionPlantelService,
  deleteConfiguracionPlantelService,
} from "../../../services/planteles/talentosPlantel.service";

// Async thunks
export const loadConfiguracionesPlantel = createAsyncThunk(
  "phraseBuilderPlantel/loadConfiguraciones",
  async (plantelId) => {
    const response = await getConfiguracionesPlantelService(plantelId);
    return response;
  }
);

export const saveConfigurationPlantel = createAsyncThunk(
  "phraseBuilderPlantel/saveConfiguration",
  async ({ plantelId, frase1Id, frase2Id }, { rejectWithValue }) => {
    try {
      const response = await createConfiguracionPlantelService(plantelId, frase1Id, frase2Id);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateConfigurationPlantel = createAsyncThunk(
  "phraseBuilderPlantel/updateConfiguration",
  async ({ id, frase1Id, frase2Id }, { rejectWithValue }) => {
    try {
      const response = await updateConfiguracionPlantelService(id, frase1Id, frase2Id);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteConfigurationPlantel = createAsyncThunk(
  "phraseBuilderPlantel/deleteConfiguration",
  async (id, { rejectWithValue }) => {
    try {
      await deleteConfiguracionPlantelService(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const initialState = {
  plantelId: null,
  leftWord: null,
  rightWord: null,

  leftSearch: "",
  rightSearch: "",

  leftRandom: false,
  rightRandom: false,

  leftPhrases: [],
  rightPhrases: [],

  puzzle: {
    left: null,
    right: null,
  },

  saved: [],
  editingId: null,
  loading: false,
  error: null,
};

const phraseBuilderPlantelSlice = createSlice({
  name: "phraseBuilderPlantel",
  initialState,
  reducers: {
    setPlantelId: (state, action) => {
      state.plantelId = action.payload;
    },
    setLeftWord: (state, action) => {
      state.leftWord = action.payload;
    },
    setRightWord: (state, action) => {
      state.rightWord = action.payload;
    },

    setLeftPhrases: (state, action) => {
      state.leftPhrases = action.payload;
    },
    setRightPhrases: (state, action) => {
      state.rightPhrases = action.payload;
    },

    setLeftRandom: (state, action) => {
      state.leftRandom = action.payload;
    },
    setRightRandom: (state, action) => {
      state.rightRandom = action.payload;
    },

    setLeftSearch: (state, action) => {
      state.leftSearch = action.payload;
    },
    setRightSearch: (state, action) => {
      state.rightSearch = action.payload;
    },

    setPuzzlePiece: (state, action) => {
      const { side, item } = action.payload;
      state.puzzle[side] = item;
    },

    removePuzzlePiece: (state, action) => {
      state.puzzle[action.payload] = null;
    },

    editSaved: (state, action) => {
      const config = state.saved[action.payload];
      // Mapear los datos de la configuración al puzzle
      state.puzzle = {
        left: {
          id: config.frase_1_detalle.id,
          text: config.frase_1_detalle.frase,
          side: "left",
        },
        right: {
          id: config.frase_2_detalle.id,
          text: config.frase_2_detalle.frase,
          side: "right",
        },
      };
      state.editingId = config.id;
    },

    clearPuzzle: (state) => {
      state.puzzle = { left: null, right: null };
      state.editingId = null;
    },

    resetPlantelState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // Load configuraciones
      .addCase(loadConfiguracionesPlantel.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadConfiguracionesPlantel.fulfilled, (state, action) => {
        state.loading = false;
        state.saved = action.payload;
      })
      .addCase(loadConfiguracionesPlantel.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Save configuration
      .addCase(saveConfigurationPlantel.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(saveConfigurationPlantel.fulfilled, (state, action) => {
        state.loading = false;
        state.saved.push(action.payload);
        state.puzzle = { left: null, right: null };
        state.editingId = null;
      })
      .addCase(saveConfigurationPlantel.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update configuration
      .addCase(updateConfigurationPlantel.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateConfigurationPlantel.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.saved.findIndex((s) => s.id === action.payload.id);
        if (index !== -1) {
          state.saved[index] = action.payload;
        }
        state.puzzle = { left: null, right: null };
        state.editingId = null;
      })
      .addCase(updateConfigurationPlantel.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete configuration
      .addCase(deleteConfigurationPlantel.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteConfigurationPlantel.fulfilled, (state, action) => {
        state.loading = false;
        state.saved = state.saved.filter((s) => s.id !== action.payload);
      })
      .addCase(deleteConfigurationPlantel.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  setPlantelId,
  setLeftWord,
  setRightWord,
  setLeftPhrases,
  setRightPhrases,
  setLeftRandom,
  setRightRandom,
  setLeftSearch,
  setRightSearch,
  setPuzzlePiece,
  removePuzzlePiece,
  editSaved,
  clearPuzzle,
  resetPlantelState,
} = phraseBuilderPlantelSlice.actions;

export default phraseBuilderPlantelSlice.reducer;
